/**
 * Which state to take after winning a hand (AI-06). Called before the hand is scored, so the choice
 * also sets this hand's contig bonus.
 *
 * Each unclaimed state is valued, in points, as:
 *   immediate  +1 if it borders a state we own (exactly one bonus, however many neighbours);
 *   future     our extra expected contig points from the hands left: for each future hand we might
 *              win (chance q, our smoothed win rate), the chance at least one unclaimed state still
 *              borders our territory then. Other winners claim states too, so each frontier state
 *              only survives k more hands with chance (U−1−k)/(U−1). Shrinks to nothing late on.
 *   denial     the same future value it takes away from each opponent whose frontier it is in.
 * These point changes are then turned into the match objective — U, our chance of winning the match
 * (matchUtil.ts) — so taking a contig only counts for what it does to the standings: a point matters
 * more in a close race and late, denial matters most against the players we are racing.
 * A contig on offer always comes first (Scott, 2026-10-27: the AI must not pass up its own contig
 * just to block someone else's); U then picks among the contig states, or among all of them when
 * none borders us — which is where blocking a rival's contig comes in.
 * Exact ties (common when nothing borders anything) are broken with the seeded RNG, not list order.
 */
import { borders, STATE_BY_CODE } from "../states.ts";
import type { PublicTable } from "../whiskey.ts";
import { makeRng } from "../rng.ts";
import { matchWinProb } from "./matchUtil.ts";

export interface StateScore { state: string; immediate: number; future: number; denial: number[]; u: number }

/** Expected contig points over the coming hands from a frontier of `f` states. */
function futureContig(f: number, unclaimedAfter: number, handsLeft: number, q: number): number {
  if (f <= 0 || handsLeft <= 0 || unclaimedAfter <= 0) return 0;
  let sum = 0;
  for (let k = 1; k <= handsLeft; k++) {
    const surv = Math.max(0, (unclaimedAfter - k) / unclaimedAfter); // a given state still free at hand k
    sum += q * (1 - Math.pow(1 - surv, f));
  }
  return sum;
}

const frontier = (owned: string[], unclaimed: string[]): number => unclaimed.filter((u) => owned.some((o) => borders(u, o))).length;

export function scoreStates(me: number, unclaimed: string[], table: PublicTable, winRates: number[], handPoints: number[]): StateScore[] {
  const H = table.handsAfterThis;
  const pts0 = table.players.map((p, i) => p.score + (handPoints[i] ?? 0));
  const states0 = table.players.map((p, i) => p.states.length + (i === me ? 1 : 0));
  return unclaimed.filter((s) => STATE_BY_CODE.has(s)).map((s) => {
    const mine = table.players[me].states;
    const rest = unclaimed.filter((u) => u !== s);
    const immediate = mine.some((o) => borders(s, o)) ? 1 : 0;
    const fNow = frontier(mine, rest), fWith = frontier([...mine, s], rest);
    const future = futureContig(fWith, rest.length, H, winRates[me]) - futureContig(fNow, rest.length, H, winRates[me]);
    const denial = table.players.map((p, j) => {
      if (j === me) return 0;
      const before = frontier(p.states, unclaimed);
      const after = frontier(p.states, rest);
      return before === after ? 0 : futureContig(before, rest.length, H, winRates[j]) - futureContig(after, rest.length, H, winRates[j]);
    });
    const pts = pts0.map((x, j) => (j === me ? x + immediate + future : x - denial[j]));
    return { state: s, immediate, future, denial, u: matchWinProb(me, pts, states0, H) };
  });
}

export function chooseState(me: number, unclaimed: string[], table: PublicTable, winRates: number[], handPoints: number[], seed: number): { state: string; scores: StateScore[] } {
  const scores = scoreStates(me, unclaimed, table, winRates, handPoints);
  const rng = makeRng(seed);
  const contigs = scores.filter((x) => x.immediate === 1);
  // order: U, then our own points (decides between options when U is flat, e.g. a decided match), then denial
  const key = (x: StateScore) => [x.u, x.immediate + x.future, x.denial.reduce((a, b) => a + b, 0)];
  let best: StateScore[] = [];
  for (const x of contigs.length ? contigs : scores) {
    if (!best.length) { best = [x]; continue; }
    const a = key(x), b = key(best[0]);
    let cmp = 0;
    for (let i = 0; i < a.length && !cmp; i++) cmp = Math.abs(a[i] - b[i]) < 1e-9 ? 0 : a[i] - b[i];
    if (cmp > 0) best = [x];
    else if (cmp === 0) best.push(x);
  }
  return { state: best[rng.int(best.length)].state, scores };
}
